/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/setup/QaPhoneSetupFlow.tsx
 * FILE_ID: JOBHUB-QA-PHONE-SETUP-001
 * PURPOSE: Quotes → Classify → Make the QA list → Review. Same handlers as the desktop card.
 */
import { useState, type ReactNode } from 'react'
import { ChevronDown, ClipboardCheck } from 'lucide-react'
import { cn, usePersistentBool } from '@quotech/ui'
import type { QuoteRow } from '../../../../jobDetailTypes'
import type { QaClassification } from '../../../../qa/classification'
import type { suggestClassification } from '../../../../qa/classification'
import type { QaCreateMode, QaWriteTarget } from '../../../JobQaSetupCard'
import {
  QA_SETUP_COLLAPSED_KEY,
  readQaSetupCollapsedStored,
  resolveQaSetupCollapsed,
} from '../../qaSetupCollapsed'
import { QaPhonePlanPreviewSheet } from './QaPhonePlanPreviewSheet'
import { QaPhonePlanStep } from './QaPhonePlanStep'
import { QaPhoneQuoteStep } from './QaPhoneQuoteStep'

const STEPS = ['Quotes', 'Make the QA list'] as const

export type QaPhoneSetupFlowProps = {
  quotes: QuoteRow[]
  quotesLoading: boolean
  quotesError?: boolean
  onRetryQuotes?: () => void
  onOpenQuote?: (q: QuoteRow) => void
  selectedSheetIds: number[]
  onToggleSheet: (id: number) => void
  onRemoveQuote?: (id: number) => void
  classification: QaClassification
  suggestion: ReturnType<typeof suggestClassification>
  onClassificationChange: (next: QaClassification) => void
  createMode: QaCreateMode
  onCreateModeChange: (mode: QaCreateMode) => void
  writeTarget?: QaWriteTarget
  onWriteTargetChange?: (target: QaWriteTarget) => void
  showWriteTarget?: boolean
  includeQuoteDerivedItems?: boolean
  onIncludeQuoteDerivedItemsChange?: (value: boolean) => void
  scopeName: string
  onScopeNameChange: (name: string) => void
  onCreate: () => void
  createPending: boolean
  createProgress: { current: number; total: number } | null
  createNotice?: string | null
  genError: string | null
  previewPanel?: ReactNode
  onRetryImport?: (quoteSheetId: number) => void
  retryingImportId?: number | null
  retryImportError?: { id: number; message: string } | null
  addQuotes?: ReactNode
  hasInspections?: boolean
  /** The Set up strip already has the title. Hide this flow's own header. */
  embedded?: boolean
  /** Set Up page scroller owns the scroll. Wins over the embedded height cap. */
  fillPage?: boolean
}

export function QaPhoneSetupFlow(props: QaPhoneSetupFlowProps) {
  const [collapsed, setCollapsed] = usePersistentBool(
    QA_SETUP_COLLAPSED_KEY,
    Boolean(props.hasInspections),
  )
  const [step, setStep] = useState(0)
  const collapsedNow = resolveQaSetupCollapsed(
    props.hasInspections,
    readQaSetupCollapsedStored(),
    collapsed,
  )
  const open = Boolean(props.embedded) || !collapsedNow || Boolean(props.previewPanel)

  return (
    <div
      className={
        props.fillPage
          ? undefined
          : props.embedded
            ? 'max-h-[45vh] overflow-y-auto'
            : 'rounded-xl border border-border bg-card p-3'
      }
      data-testid="qa-phone-setup"
    >
      {!props.embedded && (
      <button
        type="button"
        onClick={() => setCollapsed(!collapsedNow)}
        aria-expanded={open}
        className="mb-1 flex min-h-[44px] w-full items-center gap-2 text-left"
      >
        <ClipboardCheck className="h-4 w-4 text-muted-foreground" aria-hidden />
        <p className="text-sm font-semibold">Set up QA from quotes</p>
        <ChevronDown className={cn('ml-auto h-4 w-4', open && 'rotate-180')} aria-hidden />
      </button>
      )}
      {open && (
        <>
          <div className="mb-3 flex gap-1">
            {STEPS.map((label, index) => (
              <button
                key={label}
                type="button"
                onClick={() => setStep(index)}
                className={cn(
                  'min-h-[44px] flex-1 rounded-lg px-2 text-[11px] font-semibold',
                  step === index ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {props.quotesLoading && props.quotes.length === 0 ? (
            <p className="text-xs text-muted-foreground">Loading quotes…</p>
          ) : props.quotesError ? (
            <>
              {props.addQuotes}
              <p className="mt-2 text-xs text-destructive">Could not load quotes.</p>
              {props.onRetryQuotes && (
                <button type="button" onClick={props.onRetryQuotes} className="mt-1 text-xs font-medium text-primary">
                  Retry
                </button>
              )}
            </>
          ) : props.quotes.length === 0 ? (
            <>
              {props.addQuotes}
              <p className="mt-2 text-xs text-muted-foreground">
                No quotes on this job yet. Add a quote and it will appear here.
              </p>
            </>
          ) : (
            <>
              {step === 0 && (
                <QaPhoneQuoteStep
                  quotes={props.quotes}
                  selectedSheetIds={props.selectedSheetIds}
                  onToggleSheet={props.onToggleSheet}
                  onRemoveQuote={props.onRemoveQuote}
                  onOpenQuote={props.onOpenQuote}
                  addQuotes={props.addQuotes}
                  onRetryImport={props.onRetryImport}
                  retryingImportId={props.retryingImportId}
                  retryImportError={props.retryImportError}
                />
              )}
              {step === 1 && (
                <QaPhonePlanStep
                  createMode={props.createMode}
                  onCreateModeChange={props.onCreateModeChange}
                  writeTarget={props.writeTarget}
                  onWriteTargetChange={props.onWriteTargetChange}
                  showWriteTarget={props.showWriteTarget}
                  selectedCount={props.selectedSheetIds.length}
                  onCreate={props.onCreate}
                  includeQuoteDerivedItems={props.includeQuoteDerivedItems}
                  onIncludeQuoteDerivedItemsChange={props.onIncludeQuoteDerivedItemsChange}
                  scopeName={props.scopeName}
                  onScopeNameChange={props.onScopeNameChange}
                  createPending={props.createPending}
                />
              )}
            </>
          )}
          {props.genError && <p className="mt-2 text-xs text-destructive">{props.genError}</p>}
          {props.createNotice ? (
            <p role="status" className="mt-2 text-xs text-amber-700 dark:text-amber-300">{props.createNotice}</p>
          ) : null}
        </>
      )}
      <QaPhonePlanPreviewSheet
        open={Boolean(props.previewPanel)}
        onClose={() => undefined}
      >
        {props.previewPanel}
      </QaPhonePlanPreviewSheet>
    </div>
  )
}
